// Encrypted offline mutation queue for the Worker app.
// Queued actions are encrypted with a per-device AES-GCM key (Web Crypto,
// non-extractable) held in IndexedDB, then replayed to the backend when online.
const DB_NAME = "prydes_worker_offline";
const DB_VERSION = 2;
const STORE_QUEUE = "queue";
const STORE_META = "meta";
const STORE_DRAFTS = "drafts";
const STORE_SHIFTS = "shiftcache";
const KEY_ID = "device-aes-key";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE))
        db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
      if (!db.objectStoreNames.contains(STORE_META))
        db.createObjectStore(STORE_META, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_DRAFTS))
        db.createObjectStore(STORE_DRAFTS, { keyPath: "key" });
      if (!db.objectStoreNames.contains(STORE_SHIFTS))
        db.createObjectStore(STORE_SHIFTS, { keyPath: "shift" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}

function reqP(r) {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

// Get-or-create the device encryption key (kept non-extractable in IndexedDB).
async function getKey() {
  const db = await openDB();
  const existing = await reqP(tx(db, STORE_META, "readonly").get(KEY_ID));
  if (existing?.key) return existing.key;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await reqP(tx(db, STORE_META, "readwrite").put({ id: KEY_ID, key }));
  return key;
}

async function encrypt(obj) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(obj));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return { iv, ct };
}

async function decrypt(iv, ct) {
  const key = await getKey();
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(plain));
}

// Queue a mutation. `payload` is encrypted at rest.
export async function enqueue(type, shiftName, payload) {
  const db = await openDB();
  const { iv, ct } = await encrypt(payload || {});
  const item = { type, shiftName, iv, ct, createdAt: Date.now(), tries: 0, lastError: "" };
  return reqP(tx(db, STORE_QUEUE, "readwrite").add(item));
}

// All queued items (FIFO by id), payloads decrypted.
export async function getPending() {
  const db = await openDB();
  const rows = await reqP(tx(db, STORE_QUEUE, "readonly").getAll());
  const out = [];
  for (const r of rows.sort((a, b) => a.id - b.id)) {
    try {
      out.push({ id: r.id, type: r.type, shiftName: r.shiftName, createdAt: r.createdAt,
                 tries: r.tries, lastError: r.lastError, payload: await decrypt(r.iv, r.ct) });
    } catch {
      out.push({ id: r.id, type: r.type, shiftName: r.shiftName, createdAt: r.createdAt,
                 tries: r.tries, lastError: "decrypt failed", payload: null, corrupt: true });
    }
  }
  return out;
}

export async function countPending() {
  const db = await openDB();
  return reqP(tx(db, STORE_QUEUE, "readonly").count());
}

export async function remove(id) {
  const db = await openDB();
  return reqP(tx(db, STORE_QUEUE, "readwrite").delete(id));
}

export async function markError(id, msg) {
  const db = await openDB();
  const store = tx(db, STORE_QUEUE, "readwrite");
  const row = await reqP(store.get(id));
  if (row) { row.tries = (row.tries || 0) + 1; row.lastError = String(msg || "").slice(0, 200); await reqP(store.put(row)); }
}

// True when the error looks like a lost connection (request sent, no response),
// so we should queue instead of surfacing a hard failure.
export function isOfflineError(e) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!e) return false;
  if (e.code === "ERR_NETWORK") return true;
  return !!(e.request && !e.response);
}

// ---- Shift-note drafts (encrypted, so half-typed notes survive offline) ----
export async function saveDraft(shiftName, participant, fields) {
  const db = await openDB();
  const { iv, ct } = await encrypt(fields || {});
  return reqP(tx(db, STORE_DRAFTS, "readwrite").put(
    { key: `${shiftName}::${participant}`, shiftName, participant, iv, ct, updatedAt: Date.now() }));
}

export async function getDrafts(shiftName) {
  const db = await openDB();
  const rows = await reqP(tx(db, STORE_DRAFTS, "readonly").getAll());
  const out = {};
  for (const r of rows) {
    if (r.shiftName !== shiftName) continue;
    try { out[r.participant] = await decrypt(r.iv, r.ct); } catch { /* skip corrupt */ }
  }
  return out;
}

export async function clearDrafts(shiftName) {
  const db = await openDB();
  const store = tx(db, STORE_DRAFTS, "readwrite");
  const rows = await reqP(store.getAll());
  await Promise.all(rows.filter((r) => r.shiftName === shiftName).map((r) => reqP(store.delete(r.key))));
}

// ---- Shift-detail cache (encrypted) so a shift can be reopened offline ----
export async function cacheShift(shiftName, detail) {
  const db = await openDB();
  const { iv, ct } = await encrypt(detail || {});
  return reqP(tx(db, STORE_SHIFTS, "readwrite").put({ shift: shiftName, iv, ct, cachedAt: Date.now() }));
}

export async function getCachedShift(shiftName) {
  const db = await openDB();
  const row = await reqP(tx(db, STORE_SHIFTS, "readonly").get(shiftName));
  if (!row) return null;
  try { return await decrypt(row.iv, row.ct); } catch { return null; }
}

// Wiped when the session ends: cached shifts hold participant details and can
// be re-fetched, so they must not outlive the sign-in (or carry over to the
// next person who signs in on this device). Queued changes and note drafts are
// unsynced work and are kept.
export async function clearShiftCache() {
  const db = await openDB();
  return reqP(tx(db, STORE_SHIFTS, "readwrite").clear());
}
